import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    MagnifyingGlassIcon,
    EyeIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    UserIcon,
    PhoneIcon,
    EnvelopeIcon,
    XMarkIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline'
import { enquiryAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import PermissionWrapper from '../../components/Admin/PermissionWrapper'
import toast from 'react-hot-toast'

const AdminEnquiries = () => {
    return (
        <PermissionWrapper requiredPermission="enquiries">
            <AdminEnquiriesContent />
        </PermissionWrapper>
    )
}

const AdminEnquiriesContent = () => {
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        source: '',
        page: 1,
        limit: 10,
        sort: 'createdAt',
        startDate: '',
        endDate: ''
    })
    const [selectedEnquiry, setSelectedEnquiry] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const queryClient = useQueryClient()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-enquiries', filters],
        queryFn: () => {
            const params = { ...filters };
            // If no source is selected, exclude contact_page to show the other three types as requested
            if (!params.source) {
                params.source = ['enquiry', 'brochure', 'counselling', 'consultation'];
            }
            return enquiryAPI.getEnquiries(params);
        },
        keepPreviousData: true,
    })

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status }) => enquiryAPI.updateEnquiryStatus(id, status),
        onSuccess: () => {
            toast.success('Enquiry status updated successfully!')
            queryClient.invalidateQueries(['admin-enquiries'])
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update enquiry status')
        },
    })

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }))
    }

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }))
    }

    const handleViewEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry)
        setIsModalOpen(true)
    }

    const handleStatusUpdate = (id, status) => {
        updateStatusMutation.mutate({ id, status })
    }

    const handleExport = async () => {
        try {
            const params = { ...filters };
            if (!params.source) {
                params.source = ['enquiry', 'brochure', 'counselling', 'consultation'];
            }
            delete params.page;
            delete params.limit;

            const response = await enquiryAPI.exportEnquiries(params);
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `enquiries_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Enquiries exported successfully');
        } catch (error) {
            toast.error('Failed to export enquiries');
        }
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedEnquiry(null)
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load enquiries" onRetry={refetch} />
            </div>
        )
    }

    const enquiries = data?.data?.enquiries || []
    const pagination = data?.data?.pagination || data?.data?.metadata || {}

    const getStatusColor = (status) => {
        const colors = {
            'new': 'bg-blue-100 text-blue-800',
            'contacted': 'bg-yellow-100 text-yellow-800',
            'qualified': 'bg-green-100 text-green-800',
            'converted': 'bg-purple-100 text-purple-800',
            'rejected': 'bg-red-100 text-red-800',
            'follow_up': 'bg-orange-100 text-orange-800',
        }
        return colors[status] || 'bg-gray-100 text-gray-800'
    }

    const getSourceColor = (source) => {
        const colors = {
            'enquiry': 'bg-cyan-100 text-cyan-800',
            'brochure': 'bg-orange-100 text-orange-800',
            'counselling': 'bg-emerald-100 text-emerald-800',
            'consultation': 'bg-emerald-100 text-emerald-800',
            'website': 'bg-gray-100 text-gray-800',
            'banner': 'bg-blue-100 text-blue-800',
            'course_page': 'bg-green-100 text-green-800',
            'university_page': 'bg-purple-100 text-purple-800',
            'brochure_download': 'bg-orange-100 text-orange-800',
            'api': 'bg-indigo-100 text-indigo-800',
            'referral': 'bg-pink-100 text-pink-800',
            'contact_page': 'bg-red-100 text-red-800',
        }
        return colors[source] || 'bg-gray-100 text-gray-800'
    }

    const getSourceLabel = (source) => {
        const labels = {
            'enquiry': 'Quick Enquiry',
            'brochure': 'Brochure Download',
            'brochure_download': 'Brochure Download',
            'counselling': 'Counselling',
            'consultation': 'Consultation',
            'website': 'Website',
            'banner': 'Banner',
            'course_page': 'Course Page',
            'university_page': 'University Page',
            'api': 'API',
            'referral': 'Referral',
            'contact_page': 'Contact Page',
        }
        return labels[source] || source
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'new': return <ClockIcon className="w-4 h-4" />
            case 'contacted': return <PhoneIcon className="w-4 h-4" />
            case 'qualified': return <CheckCircleIcon className="w-4 h-4" />
            case 'converted': return <CheckCircleIcon className="w-4 h-4" />
            case 'rejected': return <XCircleIcon className="w-4 h-4" />
            default: return <ClockIcon className="w-4 h-4" />
        }
    }

    return (
        <>
            <Helmet>
                <title>Manage Enquiries | Margdarshak Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Enquiries</h1>
                                <p className="text-gray-600">Manage Quick, Brochure, and Counselling enquiries</p>
                            </div>
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={handleExport}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                                >
                                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                                    Export CSV
                                </button>
                                <div className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
                                    {pagination.total_count || pagination.total || 0} Total
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border-b border-gray-200 sticky top-16 z-20">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div className="lg:col-span-1">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search name, email, phone..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <select
                                    value={filters.source}
                                    onChange={(e) => handleFilterChange('source', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="">All Types</option>
                                    <option value="enquiry">Quick Enquiry</option>
                                    <option value="brochure">Brochure Download</option>
                                    <option value="counselling">Counselling</option>
                                    <option value="consultation">Consultation</option>
                                </select>
                            </div>
                            <div>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="">All Status</option>
                                    <option value="new">New</option>
                                    <option value="contacted">Contacted</option>
                                    <option value="qualified">Qualified</option>
                                    <option value="converted">Converted</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="follow_up">Follow Up</option>
                                </select>
                            </div>
                            <div>
                                <input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="flex gap-2">
                                <input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                                <button
                                    onClick={() => setFilters({ search: '', status: '', source: '', page: 1, limit: 10, sort: 'createdAt', startDate: '', endDate: '' })}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {enquiries.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">No enquiries found</td>
                                            </tr>
                                        ) : (
                                            enquiries.map((enquiry) => (
                                                <tr key={enquiry._id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                                                                <UserIcon className="w-5 h-5 text-primary-600" />
                                                            </div>
                                                            <div>
                                                                <div className="text-sm font-semibold text-gray-900">{enquiry.name}</div>
                                                                <div className="text-xs text-gray-500">{enquiry.city}, {enquiry.state}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900 flex items-center mb-1">
                                                            <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
                                                            {enquiry.email}
                                                        </div>
                                                        <div className="text-sm text-gray-500 flex items-center">
                                                            <PhoneIcon className="w-4 h-4 text-gray-400 mr-2" />
                                                            {enquiry.phone}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-900 font-medium">
                                                            {enquiry.courseId?.title || enquiry.degreeName || enquiry.universityId?.name || 'General Enquiry'}
                                                        </div>
                                                        {enquiry.degreeLevel && (
                                                            <div className="text-xs text-gray-500 mt-1 capitalize">{enquiry.degreeLevel} Level</div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceColor(enquiry.source)}`}>
                                                            {getSourceLabel(enquiry.source)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(enquiry.status)}`}>
                                                            {getStatusIcon(enquiry.status)}
                                                            <span className="ml-1 capitalize">{enquiry.status}</span>
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {new Date(enquiry.createdAt).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <div className="flex justify-end items-center space-x-3">
                                                            <button onClick={() => handleViewEnquiry(enquiry)} className="text-blue-600 hover:text-blue-900 bg-blue-50 p-2 rounded-lg">
                                                                <EyeIcon className="w-5 h-5" />
                                                            </button>
                                                            <select
                                                                value={enquiry.status}
                                                                onChange={(e) => handleStatusUpdate(enquiry._id, e.target.value)}
                                                                className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-500 bg-white"
                                                            >
                                                                <option value="new">New</option>
                                                                <option value="contacted">Contacted</option>
                                                                <option value="qualified">Qualified</option>
                                                                <option value="converted">Converted</option>
                                                                <option value="rejected">Rejected</option>
                                                                <option value="follow_up">Follow Up</option>
                                                            </select>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {(pagination.total_pages > 1 || pagination.totalPages > 1) && (
                                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                    <div className="text-sm text-gray-700">
                                        Showing {(( (pagination.current_page || pagination.currentPage || 1) - 1) * (pagination.per_page || pagination.limit || 10)) + 1} to {' '}
                                        {Math.min((pagination.current_page || pagination.currentPage || 1) * (pagination.per_page || pagination.limit || 10), (pagination.total_count || pagination.total || 0))} of {' '}
                                        {(pagination.total_count || pagination.total || 0)} results
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handlePageChange((pagination.current_page || pagination.currentPage || 1) - 1)}
                                            disabled={(pagination.current_page || pagination.currentPage || 1) === 1}
                                            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange((pagination.current_page || pagination.currentPage || 1) + 1)}
                                            disabled={(pagination.current_page || pagination.currentPage || 1) === (pagination.total_pages || pagination.totalPages)}
                                            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* View Enquiry Modal */}
                <AnimatePresence>
                    {isModalOpen && selectedEnquiry && (
                        <div className="fixed inset-0 z-[9999] overflow-y-auto">
                            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                                <div className="fixed inset-0 transition-opacity bg-gray-500/75 backdrop-blur-sm" onClick={handleCloseModal}></div>

                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                    className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl relative z-[10000]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-900">Enquiry Details</h3>
                                            <p className="text-sm text-gray-500 mt-1">Ref ID: {selectedEnquiry._id}</p>
                                        </div>
                                        <button onClick={handleCloseModal} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors">
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-gray-100">
                                            <div>
                                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Student info</h4>
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Full Name</label>
                                                        <p className="text-sm font-semibold text-gray-900">{selectedEnquiry.name}</p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Email Address</label>
                                                        <p className="text-sm font-semibold text-gray-900">{selectedEnquiry.email}</p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Phone Number</label>
                                                        <p className="text-sm font-semibold text-gray-900">{selectedEnquiry.phone}</p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Location</label>
                                                        <p className="text-sm font-semibold text-gray-900">{selectedEnquiry.city}, {selectedEnquiry.state}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Enquiry Details</h4>
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Source Type</label>
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceColor(selectedEnquiry.source)} mt-1`}>
                                                            {getSourceLabel(selectedEnquiry.source)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Interested In</label>
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            {selectedEnquiry.courseId?.title || selectedEnquiry.degreeName || selectedEnquiry.universityId?.name || 'General Inquiry'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Current Status</label>
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedEnquiry.status)} mt-1`}>
                                                            {selectedEnquiry.status}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-500">Submitted On</label>
                                                        <p className="text-sm font-semibold text-gray-900">{new Date(selectedEnquiry.createdAt).toLocaleString()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {selectedEnquiry.message && (
                                            <div>
                                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Message/Requirements</h4>
                                                <div className="p-4 bg-gray-50 rounded-xl text-sm text-gray-700 border border-gray-100 whitespace-pre-wrap">
                                                    {selectedEnquiry.message}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex justify-end pt-4 space-x-3">
                                            <button onClick={handleCloseModal} className="px-6 py-2 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                                                Close
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    handleStatusUpdate(selectedEnquiry._id, 'contacted')
                                                    handleCloseModal()
                                                }}
                                                className="px-6 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 shadow-md shadow-primary-200 transition-all flex items-center"
                                            >
                                                <PhoneIcon className="w-4 h-4 mr-2" />
                                                Mark as Contacted
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </>
    )
}

export default AdminEnquiries
